import { z } from "zod";
import { memoryEntrySchema, memoryLifecycleIdempotencyKeySchema, } from "./memories.js";
import { memoryFeedbackEventSchema, memoryLifecycleReceiptSchema, } from "./memory-lifecycle.js";
const identifierSchema = z.string().trim().min(1).max(2_048);
const reasonSchema = z.string().trim().min(1).max(500);
const summarySchema = z.string().trim().min(1).max(500);
/**
 * Privacy-safe, authoritative readback for one immutable Canonical Episode.
 * Searchable episode text and evidence payloads deliberately stay out of this
 * management surface; the trace graph already carries the evidence shown to
 * the user.
 */
export const canonicalMemoryEpisodeSchema = z.object({
    episodeId: identifierSchema,
    sourceSessionId: identifierSchema,
    sourceRunId: identifierSchema,
    sourceStatus: z.enum(["completed", "failed"]),
    sourceFinishedAt: z.number().int().nonnegative(),
    servingState: z.literal("eligible"),
    suppressed: z.boolean(),
}).strict();
export const canonicalMemoryEpisodeCapabilitiesResponseSchema = z.object({
    episode: canonicalMemoryEpisodeSchema,
    actions: z.object({
        saveAsProposedIntentional: z.boolean(),
        doNotReuse: z.boolean(),
    }).strict(),
    confirmationRequired: z.literal(true),
    idempotencyRequired: z.literal(true),
}).strict();
export const saveCanonicalMemoryEpisodeInputSchema = z.object({
    confirmation: z.literal(true),
    idempotencyKey: memoryLifecycleIdempotencyKeySchema,
    summary: summarySchema,
}).strict();
function savedMemoryMatchesEpisode(value) {
    return value.memory.status === "proposed" &&
        value.memory.scopeType === "user" &&
        value.memory.sourceSessionId === value.episode.sourceSessionId &&
        value.memory.sourceRunId === value.episode.sourceRunId &&
        value.memory.value.canonicalEpisodeId === value.episode.episodeId &&
        value.memory.value.sourceRunId === value.episode.sourceRunId;
}
export const canonicalMemoryEpisodeSaveResultSchema = z.object({
    episode: canonicalMemoryEpisodeSchema,
    memory: memoryEntrySchema,
}).strict().superRefine((result, context) => {
    if (!savedMemoryMatchesEpisode(result)) {
        context.addIssue({
            code: "custom",
            path: ["memory"],
            message: "saved memory must be a proposed user memory bound to the exact canonical episode source",
        });
    }
});
export const saveCanonicalMemoryEpisodeResponseSchema = z.object({
    episode: canonicalMemoryEpisodeSchema,
    memory: memoryEntrySchema,
    receipt: memoryLifecycleReceiptSchema,
}).strict().superRefine((response, context) => {
    if (!savedMemoryMatchesEpisode(response)) {
        context.addIssue({
            code: "custom",
            path: ["memory"],
            message: "saved memory must be a proposed user memory bound to the exact canonical episode source",
        });
    }
    if (response.receipt.action !== "create" ||
        response.receipt.targetId !== response.episode.episodeId) {
        context.addIssue({
            code: "custom",
            path: ["receipt"],
            message: "save receipt must target the exact canonical episode",
        });
    }
});
export const doNotReuseCanonicalMemoryEpisodeInputSchema = z.object({
    confirmation: z.literal(true),
    idempotencyKey: memoryLifecycleIdempotencyKeySchema,
    reason: reasonSchema,
}).strict();
export const canonicalMemoryEpisodeSuppressionResultSchema = z.object({
    episode: canonicalMemoryEpisodeSchema,
    event: memoryFeedbackEventSchema,
}).strict();
export const doNotReuseCanonicalMemoryEpisodeResponseSchema = canonicalMemoryEpisodeSuppressionResultSchema.extend({
    receipt: memoryLifecycleReceiptSchema,
}).strict().superRefine((response, context) => {
    if (!response.episode.suppressed) {
        context.addIssue({
            code: "custom",
            path: ["episode", "suppressed"],
            message: "do-not-reuse readback must show the episode as suppressed",
        });
    }
    if (response.event.targetKind !== "canonical_episode" ||
        response.event.targetRef !== response.episode.episodeId ||
        response.event.outcome !== "do_not_reuse") {
        context.addIssue({
            code: "custom",
            path: ["event"],
            message: "feedback must suppress the exact returned canonical episode",
        });
    }
});
